import { toFacePayloadData } from '@/constants/emoji';
import { toMessageTimestamp } from '@/utils/messageTime';
function isHttpUrl(value) {
    return /^https?:\/\//i.test(value);
}
export function isImageMessageType(msgType) {
    if (!msgType)
        return false;
    const type = msgType.trim();
    return type === 'TIMImageElem' || type === 'TIMImage' || type === 'image' || /image/i.test(type);
}
/** 从消息字段或 content 中解析图片 URL */
export function extractMessageImageUrl(message) {
    const candidates = [message.imageUrl, message.mediaUrl, message.picUrl, message.url, message.content];
    for (const candidate of candidates) {
        if (typeof candidate !== 'string')
            continue;
        const value = candidate.trim();
        if (!value)
            continue;
        if (isHttpUrl(value) || value.startsWith('blob:'))
            return value;
    }
    const content = message.content?.trim() ?? '';
    if (content.startsWith('{') || content.startsWith('[')) {
        try {
            const parsed = JSON.parse(content);
            const nested = [parsed.url, parsed.imageUrl, parsed.image_url, parsed.picUrl, parsed.mediaUrl];
            for (const item of nested) {
                if (typeof item === 'string' && item.trim() && (isHttpUrl(item) || item.startsWith('blob:'))) {
                    return item.trim();
                }
            }
        }
        catch {
            // ignore invalid json content
        }
    }
    return '';
}
/** 统一图片消息的 kind / imageUrl，兼容后端接口与 TIM 回显 */
export function normalizeDisplayMessage(message) {
    const content = message.content ?? '';
    const imageUrl = extractMessageImageUrl(message);
    const isImage = message.kind === 'image' ||
        isImageMessageType(message.msgType) ||
        (content === '[图片]' && !!imageUrl);
    if (!isImage) {
        return { ...message, content };
    }
    return {
        ...message,
        kind: 'image',
        content: content || '[图片]',
        imageUrl,
    };
}
export function revokeBlobImageUrl(url) {
    if (url?.startsWith('blob:')) {
        URL.revokeObjectURL(url);
    }
}
function messageKey(message) {
    if (message.msgId)
        return message.msgId;
    return `${message.fromImUid ?? ''}|${message.msgTime ?? ''}|${message.content ?? ''}`;
}
function isRecentPendingMessage(pending, incoming) {
    const timeDiff = Math.abs(toMessageTimestamp(pending.msgTime) - toMessageTimestamp(incoming.msgTime));
    return timeDiff <= 120000;
}
function sameFaceData(a, b) {
    if (!a || !b)
        return false;
    return toFacePayloadData(a) === toFacePayloadData(b);
}
function sameStickerList(a, b) {
    const left = a ?? [];
    const right = b ?? [];
    if (left.length !== right.length)
        return false;
    return left.every((item, index) => item === right[index]);
}
/** 匹配本地乐观消息，避免 TIM 回显重复展示 */
function findOptimisticIndex(list, incoming) {
    return list.findIndex((pending) => {
        if (pending.msgId || !pending.fromSelf || !incoming.fromSelf)
            return false;
        if (pending.fromImUid !== incoming.fromImUid)
            return false;
        if (!isRecentPendingMessage(pending, incoming))
            return false;
        if (incoming.kind === 'mixed') {
            return (pending.kind === 'mixed' &&
                pending.content === incoming.content &&
                sameStickerList(pending.stickers, incoming.stickers));
        }
        if (incoming.kind === 'text' || incoming.msgType === 'TIMTextElem') {
            return ((pending.kind === 'text' || pending.kind === 'mixed') &&
                pending.content === incoming.content);
        }
        if (incoming.kind === 'face') {
            if (pending.kind === 'face' && sameFaceData(pending.faceData, incoming.faceData))
                return true;
            if (pending.kind === 'mixed' &&
                pending.stickers?.some((sticker) => sameFaceData(sticker, incoming.faceData))) {
                return true;
            }
            return false;
        }
        if (incoming.kind === 'image' || isImageMessageType(incoming.msgType)) {
            return pending.kind === 'image' || isImageMessageType(pending.msgType);
        }
        return false;
    });
}
export function mergeChatMessages(apiRows, timRows, selfImUid) {
    const map = new Map();
    const rows = [...(apiRows ?? []), ...(timRows ?? [])];
    for (const row of rows) {
        if (!row)
            continue;
        const normalized = normalizeDisplayMessage({
            ...row,
            content: row.content ?? '',
            fromSelf: row.fromImUid === selfImUid,
        });
        map.set(messageKey(normalized), normalized);
    }
    return Array.from(map.values()).sort((a, b) => toMessageTimestamp(a.msgTime) - toMessageTimestamp(b.msgTime));
}
/** 合并自己发送消息的 TIM 回显，避免重复追加 */
export function mergeSelfEchoMessage(list, incoming) {
    const normalized = normalizeDisplayMessage(incoming);
    if (!normalized.fromSelf)
        return false;
    if (normalized.msgId && list.some((item) => item.msgId === normalized.msgId))
        return true;
    const optimisticIndex = findOptimisticIndex(list, normalized);
    if (optimisticIndex >= 0) {
        const pending = list[optimisticIndex];
        if (pending.kind === 'mixed') {
            if (normalized.kind === 'mixed') {
                list[optimisticIndex] = {
                    ...normalized,
                    kind: 'mixed',
                    stickers: normalized.stickers ?? pending.stickers,
                };
                return true;
            }
            if (normalized.kind === 'text') {
                list[optimisticIndex] = { ...pending, msgId: normalized.msgId };
            }
            return true;
        }
        const nextImageUrl = normalized.imageUrl || pending.imageUrl;
        if (pending.imageUrl?.startsWith('blob:') && nextImageUrl && !nextImageUrl.startsWith('blob:')) {
            revokeBlobImageUrl(pending.imageUrl);
        }
        list[optimisticIndex] = normalizeDisplayMessage({
            ...normalized,
            kind: pending.kind === 'image' || normalized.kind === 'image' ? 'image' : pending.kind,
            imageUrl: nextImageUrl,
            stickers: pending.stickers,
        });
        return true;
    }
    return true;
}
export function appendChatMessage(list, message, selfImUid) {
    const normalized = normalizeDisplayMessage({
        ...message,
        content: message.content ?? '',
        fromSelf: message.fromImUid === selfImUid,
    });
    if (normalized.fromSelf) {
        mergeSelfEchoMessage(list, normalized);
        return;
    }
    if (list.some((item) => messageKey(item) === messageKey(normalized)))
        return;
    list.push(normalized);
}
